<!-- [팝업] 웹 리소스 업로드 -->
<template>
  <b-modal
    v-model="showWebResourceFileUpload"
    cancel-variant="outline-secondary"
    centered
    ok-title="업로드"
    cancel-title="취소"
    size="lg"
    title="리소스 업로드"
    no-close-on-esc
    no-close-on-backdrop
    @ok.prevent="submit()"
    @close.prevent="close()"
    @cancel.prevent="close()"
  >
    <b-row>
      <b-col
        cols="12"
        md="12"
      >
        <b-form-group
          label="리소스 파일"
          label-for="web-resource-files"
        >
          <b-form-file
            ref="fileInput"
            multiple
            :placeholder="placeholderText"
            :state="!isFileBlocked"
            :file-name-formatter="formatNames"
            :accept="acceptFileExt"
            @input="inputFiles"
          />
        </b-form-group></b-col></b-row>
    <b-row>
      <b-col
        cols="12"
        md="12"
      >
        <div
          v-for="(item, idx) in webResourceDataList"
          :key="idx"
        >
          <b-input-group>
            <b-input-group-prepend>
              <b-input-group-text style="width: 20rem;">
                {{ item.WebResourceFileName }}
              </b-input-group-text>
            </b-input-group-prepend>
            <b-form-input
              v-model="item.Title"
              placeholder="이용 목적을 입력해주세요 (최대 250자)"
              maxlength="250"
            />
            <b-input-group-append>
              <b-input-group-text
                style="width: 6rem;"
              >
                {{ (item.WebResourceFileSize / (1024 * 1024)).toFixed(2) }} MB
              </b-input-group-text>
            </b-input-group-append>
          </b-input-group>
        </div>
      </b-col>
    </b-row>

  </b-modal>
</template>

<script>
import axios from '@axios'
import { ref, onMounted, getCurrentInstance } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BFormFile, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BInputGroupText,
} from 'bootstrap-vue'

export default {
  components: {
    BFormFile,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BInputGroupText,
  },

  props: {
    showWebResourceFileUpload: {
      type: Boolean,
      required: true,
    },
  },

  setup(props, { emit }) {
    onMounted(() => {
      fetchWebResourceType()
    })

    const instance = getCurrentInstance()
    const refs = instance.proxy.$refs
    const toast = useToast()
    const pushToast = (variant, title) => {
      let icon
      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else {
        icon = 'AlertTriangleIcon'
      }

      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    const acceptFileExt = ref(".jpg, .jpeg, .png")
    const placeholderText = ref("5MB 이하의 JPG, JPEG, PNG 파일만 지원합니다.")
    const webResourceDataList = ref([])
    const isFileBlocked = ref(false)

    // Set Codes
    const codes = ref({
      webResourceType: [],
      webResourceUsageType: [],
    })

    const fetchWebResourceType = () => {
      axios.get(`/fa/web-resource/codes`)
        .then(rs => {
          codes.value.webResourceType = rs.data.webResourceType
          codes.value.webResourceUsageType = rs.data.webResourceUsageType

          resetWebResourceFileData() // 가져온 webResourceType 정보로 초기화
        })
        .catch(err => {
          console.log(err)
          pushToast('danger', '코드 데이터를 불러오는데 실패하였습니다.')
        })
    }
    // Set Codes End.

    // Query Data
    const query = ref({
      webResourceType: [], // [{ code: 11, label: "JPG" }, { code: 12, label: "JPEG" }, { code: 13, label: "PNG" }]
      webResourceUsageType: 100, // 100: 데스크인포
    })
    // Query Data End.

    // File config
    const webResourceFiles = ref([]) // 웹 리소스 파일 목록
    // File config End.

    // 파일 선택 후 placeholderText 교체
    const formatNames = () => {
      if (isFileBlocked.value) {
        return `total ${webResourceFiles.value.length} files selected : 지원되지 않는 파일이 제외됨`
      }
      return `total ${webResourceFiles.value.length} files selected`
    }

    const inputFiles = files => {
      const passExt = query.value.webResourceType.map(x => `.${x.label.toLowerCase()}`)

      const filteredFiles = files
        .filter(file => passExt.some(ext => file.name.toLowerCase().endsWith(ext)))
        .filter(file => file.size < 5248122)

      // 파일이 필터링된 경우 업로드 조건 강조를 해제
      if (filteredFiles.length < files.length) {
        isFileBlocked.value = true
      } else {
        isFileBlocked.value = false
      }

      // 이미 선택된 파일과 중복되지 않도록 추가
      const newFiles = filteredFiles.filter(file => !webResourceFiles.value.some(existingFile => existingFile.name === file.name))

      // 새로운 파일이 있는 경우에만 추가
      if (newFiles.length > 0) {
        for (let i = 0; i < newFiles.length; i += 1) {
          // html인 경우 한글 깨짐 방지를 위해 charset=utf-8 지정
          if (newFiles[i].type === "text/html") {
            // 타입이 text/html이면 charset 추가를 위해 새로운 File 객체 생성
            const newFile = new File([newFiles[i]], newFiles[i].name, {
              type: "text/html; charset=utf-8",
              lastModified: newFiles[i].lastModified,
            })
            newFiles[i] = newFile // utf-8 파일로 변경
          }
          // webResourceDataList 채우기
          webResourceDataList.value.push({
            // Id: 0, // 등록 시
            WebResourceType: query.value.webResourceType.find(x => x.label.toLowerCase() === newFiles[i].name.split(".").pop().toLowerCase()).code, // 확장자에 맞는 typeCode 지정
            WebResourceUsageType: query.value.webResourceUsageType,
            Title: '',
            WebResourceFileName: newFiles[i].name,
            WebResourceFileSize: newFiles[i].size,
          })
        }

        webResourceFiles.value = [...webResourceFiles.value, ...newFiles]
      }
    }

    const resetWebResourceFileData = () => {
      query.value.webResourceType = codes.value.webResourceType

      acceptFileExt.value = query.value.webResourceType.map(x => `.${x.label.toLowerCase()}`).join(", ")
      placeholderText.value = `5MB 이하의 ${query.value.webResourceType.map(x => x.label.toUpperCase()).join(", ")} 파일만 지원합니다.`

      webResourceDataList.value = []
      webResourceFiles.value = []
      isFileBlocked.value = false

      // 같은 파일을 다시 선택할 수 있도록 초기화
      const input = refs.fileInput
      if (input) {
        input.reset()
      }
    }

    const uploadWebResource = () => {
      const formdata = new FormData()

      // console.log(webResourceFiles.value)

      formdata.append("jsonWebResourceUploadList", JSON.stringify(webResourceDataList.value))

      for (let i = 0; i < webResourceFiles.value.length; i += 1) {
        formdata.append("webResourceFileList", webResourceFiles.value[i])
      }

      axios.post("/fa/web-resource", formdata, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then(rs => {
          webResourceDataList.value = []
          if (rs) {
            pushToast('success', '리소스 업로드 완료')
            resetWebResourceFileData()
            emit('close', 1)
          }
        })
        .catch(err => {
          if (err.response.status === 400) {
            pushToast('warning', err.response.data)
          } else {
            pushToast('danger', '리소스를 업로드하는 과정에서 오류가 발생하였습니다.')
          }
          webResourceDataList.value = []
          resetWebResourceFileData()
          emit('close', null)
        })
    }

    const close = () => {
      resetWebResourceFileData()
      emit('close', null)
    }

    const submit = () => {
      if (webResourceFiles.value === null || webResourceFiles.value.length === 0 || webResourceDataList.value.length === 0) {
        toast({
          component: ToastificationContent,
          props: {
            title: '업로드할 파일을 선택하세요',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }
      uploadWebResource()
    }

    return {
      query,
      codes,
      close,
      submit,
      uploadWebResource,
      webResourceFiles,
      resetWebResourceFileData,
      inputFiles,
      acceptFileExt,
      placeholderText,
      formatNames,
      webResourceDataList,
      isFileBlocked,
    }
  },
}
</script>
